/**
 * useDeckViewState Hook
 *
 * Zentraler State für die Deck-Ansicht: Filtert Karten, gruppiert sie per Strategy
 * und verwaltet die Gruppen-Reihenfolge.
 *
 * Zwei-Schichten-Architektur:
 * 1. Excluded Karten abspalten → excludedGroups
 * 2. Rest per strategy.groupFn gruppieren → displayGroups
 * 3. Zusammenführen zu einer einheitlichen groups-Liste
 *
 * groups enthält { name, cards, type } Objekte.
 * type: 'display' = Anzeige-Gruppe | 'excluded' = excluded Kategorie
 * Im categories-Modus haben Einträge stattdessen { name, excluded } (aus categoryOrder).
 */
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { groupCardsByStrategy, sortCards } from '../utils/cardGrouping';
import { useCategoryManagement } from './useCategoryManagement';

/**
 * @param {Object} options
 * @param {Object} options.deck - Das Deck-Objekt
 * @param {string} options.sortBy - Sortierkriterium
 * @param {Object} options.strategy - Grouping Strategy aus groupingStrategies.js
 * @param {boolean} options.showExcluded - Ausgeschlossene Kategorien anzeigen
 * @param {boolean} options.isOwnProfile - Ob der aktuelle User der Owner ist
 * @param {string} options.authToken - JWT Token für API-Calls
 * @param {boolean} [options.includeCommander=false] - Commander-Karten als erste Gruppe
 *   voranstellen, statt sie aus den Gruppen auszuschließen (mobile Ansicht)
 */
export function useDeckViewState({
  deck,
  sortBy,
  strategy,
  showExcluded,
  isOwnProfile,
  authToken,
  includeCommander = false,
}) {
  // --- Lokale Gruppen-Reihenfolge für nicht-persistente Modi ---
  const [localGroupOrder, setLocalGroupOrder] = useState([]);
  const lastStrategyKeyRef = useRef(strategy.key);

  // Reset lokale Reihenfolge bei Moduswechsel
  useEffect(() => {
    if (strategy.key !== lastStrategyKeyRef.current) {
      setLocalGroupOrder([]);
      lastStrategyKeyRef.current = strategy.key;
    }
  }, [strategy.key]);

  // --- Excluded Kategorien aus editorSettings ermitteln ---
  const savedExcludedNames = useMemo(() => {
    const saved = deck?.editorSettings?.categories;
    if (!Array.isArray(saved)) return new Set();
    return new Set(saved.filter(c => c.excluded).map(c => c.name?.toLowerCase()).filter(Boolean));
  }, [deck?.editorSettings?.categories]);

  // --- Schicht 1: Excluded Karten abspalten ---
  const { nonExcludedCards, excludedCardsByCategory } = useMemo(() => {
    const excluded = new Map();
    const rest = [];
    const allCards = deck?.cards || [];

    for (const card of allCards) {
      const cat = card.category?.toLowerCase();
      if (card.isCommander) continue;
      if (savedExcludedNames.has(cat)) {
        const originalCat = card.category;
        if (!excluded.has(originalCat)) excluded.set(originalCat, []);
        excluded.get(originalCat).push(card);
      } else {
        rest.push(card);
      }
    }

    return { nonExcludedCards: rest, excludedCardsByCategory: excluded };
  }, [deck?.cards, savedExcludedNames]);

  // --- Schicht 2: Rest per Strategy gruppieren ---
  const categorizedCards = useMemo(() => {
    if (nonExcludedCards.length === 0) return new Map();
    return groupCardsByStrategy(nonExcludedCards, strategy, sortBy);
  }, [nonExcludedCards, strategy, sortBy]);

  // --- Category Management (nur bei persistent strategies aktiv) ---
  const {
    categories,
    categoryOrder: persistedCategoryOrder,
    categoryByName,
    moveCategory: persistedMoveCategory,
    removeCategory,
    addCategory,
    renameCategory,
    excludedCategoryNames,
    commanderCategory,
  } = useCategoryManagement({
    deck,
    isOwnProfile,
    active: strategy.persistent,
  });

  // --- Lokale Gruppen-Reihenfolge (DnD ohne Speichern) ---
  const moveLocalGroup = useCallback((activeGroup, overGroup) => {
    if (activeGroup === overGroup) return;
    setLocalGroupOrder(prev => {
      const oldIndex = prev.indexOf(activeGroup);
      const newIndex = prev.indexOf(overGroup);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      return next;
    });
  }, []);

  // --- Commander-Gruppe (Opt-in): separat eingesammelt, vor beide Zweige gesetzt ---
  const commanderCards = useMemo(
    () => (deck?.cards || []).filter(c => c.isCommander),
    [deck?.cards]
  );

  // --- Einheitliche groups-Liste zusammenbauen ---
  const { groups, moveCategory } = useMemo(() => {
    const commanderGroup = includeCommander && commanderCards.length > 0
      ? [{
          id: commanderCategory?.id ?? null,
          name: 'Commander',
          cards: commanderCards,
          type: 'display',
          excluded: false,
          isCommander: true,
        }]
      : [];

    if (strategy.persistent) {
      // Categories-Modus: categoryOrder bestimmt alles (inkl. excluded)
      const visibleOrder = (showExcluded
        ? persistedCategoryOrder
        : persistedCategoryOrder.filter(c => !c.excluded)
      ).filter(c => !c.isCommander); // Commander-Zone wird separat gerendert (Spec §5.5)

      const result = visibleOrder.map(entry => ({
        id: entry.id,
        name: entry.name,
        cards: categorizedCards.get(entry.name) || excludedCardsByCategory.get(entry.name) || [],
        type: entry.excluded ? 'excluded' : 'display',
        excluded: entry.excluded,
        isCommander: false,
      }));

      // 'other' als Display-Fallback anhängen, wenn Karten ohne Kategorie da sind
      // und 'other' nicht bereits explizit im categoryOrder steht.
      const orderHasOther = persistedCategoryOrder.some(c => c.name === 'other');
      const otherCards = categorizedCards.get('other');
      if (!orderHasOther && otherCards && otherCards.length > 0) {
        result.push({ id: null, name: 'other', cards: otherCards, type: 'display', excluded: false, isCommander: false });
      }

      return { groups: [...commanderGroup, ...result], moveCategory: persistedMoveCategory };
    }

    // Nicht-persistente Modi: Strategy sortiert, excluded Kategorien am Ende
    let displayGroupNames = [...categorizedCards.keys()];
    if (strategy.sortGroupsFn) {
      displayGroupNames.sort(strategy.sortGroupsFn);
    }

    // Lokale Reihenfolge anwenden (User hat per DnD umsortiert)
    if (localGroupOrder.length > 0) {
      const localSet = new Set(localGroupOrder);
      const newNames = displayGroupNames.filter(n => !localSet.has(n));
      displayGroupNames = [...localGroupOrder.filter(n => displayGroupNames.includes(n)), ...newNames];
    }

    const displayGroups = displayGroupNames.map(name => ({
      id: categoryByName.get(name.toLowerCase())?.id ?? null,
      name,
      cards: categorizedCards.get(name) || [],
      type: 'display',
    }));

    // Excluded Kategorien anhängen (wenn sichtbar)
    const excludedGroups = [];
    if (showExcluded) {
      for (const [name, cards] of excludedCardsByCategory) {
        excludedGroups.push({
          id: categoryByName.get(name.toLowerCase())?.id ?? null,
          name,
          cards: sortCards(cards, sortBy),
          type: 'excluded',
        });
      }
      // Auch leere excluded Kategorien aus editorSettings anzeigen
      const savedOrder = deck?.editorSettings?.categories || [];
      for (const entry of savedOrder) {
        if (entry.excluded && !excludedCardsByCategory.has(entry.name)) {
          let found = false;
          for (const key of excludedCardsByCategory.keys()) {
            if (key.toLowerCase() === entry.name.toLowerCase()) { found = true; break; }
          }
          if (!found) {
            excludedGroups.push({ id: entry.id, name: entry.name, cards: [], type: 'excluded' });
          }
        }
      }
    }

    return {
      groups: [...commanderGroup, ...displayGroups, ...excludedGroups],
      moveCategory: moveLocalGroup,
    };
  }, [
    strategy, categorizedCards, excludedCardsByCategory, persistedCategoryOrder,
    persistedMoveCategory, showExcluded, localGroupOrder, moveLocalGroup,
    sortBy, deck?.editorSettings?.categories, categoryByName,
    includeCommander, commanderCards, commanderCategory,
  ]);

  // Initialisiere lokale Reihenfolge wenn nötig
  useEffect(() => {
    if (!strategy.persistent && localGroupOrder.length === 0 && categorizedCards.size > 0) {
      let names = [...categorizedCards.keys()];
      if (strategy.sortGroupsFn) names.sort(strategy.sortGroupsFn);
      setLocalGroupOrder(names);
    }
  }, [strategy, localGroupOrder.length, categorizedCards]);

  return {
    groups,
    categories,
    categoryByName,
    moveCategory,
    removeCategory,
    addCategory,
    renameCategory,
    excludedCategoryNames,
  };
}
